import { useRef, useState, useEffect, useCallback, RefObject } from 'react'

/**
 * Measures a FilterBar container and determines whether all filter buttons
 * fit. Returns `allFit` (boolean) — either every button is visible or none
 * are (the "All Filters" button takes their place).
 *
 * The hook:
 *  1. Watches the container width with a ResizeObserver
 *  2. Re-measures when `deps` change (e.g. active-label widths)
 *  3. Uses requestAnimationFrame to batch layout reads
 *
 * Layout assumptions (must match FilterBar render order):
 *   [filter-0] [filter-1] … [filter-N] [clear?] | [viewToggle]
 *   ← ─ ─ ─ ─ ─ ─ containerRef ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ →
 *
 * `measureFixedRef` points at a hidden measurement element that always
 * contains the full "expanded" right-hand zone (e.g. 3-button toggle).
 * This avoids circular measurement: the visible toggle may conditionally
 * render buttons based on `allFit`, but the measurement ref is stable.
 *
 * Items with `offsetWidth === 0` (display:none / hidden) are skipped so
 * the hook gracefully handles items that may be conditionally hidden.
 */
export function useOverflowFilters (
	containerRef: RefObject<HTMLDivElement | null>,
	itemRefs: RefObject<(HTMLDivElement | null)[]>,
	measureFixedRef: RefObject<HTMLDivElement | null>,
	deps: unknown[] = []
): { allFit: boolean; measured: boolean } {
	const [allFit, setAllFit] = useState(false)
	const [measured, setMeasured] = useState(false)
	const rafId = useRef(0)

	const measure = useCallback(() => {
		const container = containerRef.current
		const items = itemRefs.current
		const fixed = measureFixedRef.current
		if (!container || !items) return

		const containerWidth = container.offsetWidth
		const gap = 8 // gap-8 token = 8px
		const containerPaddingLeft = 12 // pl-12
		const containerPaddingRight = 8 // pr-8

		const fixedWidth = fixed ? fixed.offsetWidth : 0

		// Available width for filter buttons (minus fixed zone, container padding, gap before fixed)
		const available =
			containerWidth -
			containerPaddingLeft -
			containerPaddingRight -
			fixedWidth -
			(fixedWidth > 0 ? gap : 0)

		let usedWidth = 0
		let fits = true
		let measuredCount = 0
		for (let i = 0; i < items.length; i++) {
			const el = items[i]
			if (!el) break
			const w = el.offsetWidth
			// Skip hidden items (offsetWidth 0)
			if (w === 0) continue
			measuredCount++
			const needed = usedWidth + w + (usedWidth > 0 ? gap : 0)
			if (needed > available) {
				fits = false
				break
			}
			usedWidth = needed
		}

		// If no items were measured, don't report as fitting (refs may not be attached yet)
		if (measuredCount === 0) fits = false

		setAllFit(fits)
		setMeasured(true)
	}, [containerRef, itemRefs, measureFixedRef])

	// ResizeObserver on container
	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(rafId.current)
			rafId.current = requestAnimationFrame(measure)
		})
		ro.observe(container)
		// Initial measure
		rafId.current = requestAnimationFrame(measure)

		return () => {
			ro.disconnect()
			cancelAnimationFrame(rafId.current)
		}
	}, [containerRef, measure])

	// Re-measure when deps change (e.g. active filter labels)
	useEffect(() => {
		cancelAnimationFrame(rafId.current)
		rafId.current = requestAnimationFrame(measure)
	}, [...deps, measure]) // eslint-disable-line react-hooks/exhaustive-deps

	return { allFit, measured }
}
